import { useState } from 'react'
import { X, CheckCircle, AlertCircle } from 'lucide-react'
import api from '@/lib/api'

interface Props {
  isOpen: boolean
  returnId: number
  caseType: string
  onClose: () => void
  onSuccess: () => void
}

export default function CloseIncidentModal({ isOpen, returnId, caseType, onClose, onSuccess }: Props) {
  const [testResult, setTestResult] = useState('')
  const [testNotes, setTestNotes] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async () => {
    setSubmitting(true)
    setError('')
    try {
      await api.post(`/returns/admin/${returnId}/close-incident`, {
        test_result: testResult || undefined,
        test_notes: testNotes || undefined,
      })
      onSuccess()
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Error cerrando incidencia')
    } finally {
      setSubmitting(false)
    }
  }

  if (!isOpen) return null

  const typeLabel = caseType === 'doa' ? 'Cam-falla' : 'Rotura/Perdida'

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between p-5 border-b border-slate-200 dark:border-slate-700">
          <h2 className="text-lg font-bold text-slate-900 dark:text-white">Cerrar incidencia ({typeLabel})</h2>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {caseType === 'doa' && (
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Resultado testeo del defectuoso</label>
              <select
                value={testResult}
                onChange={(e) => setTestResult(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-sm"
              >
                <option value="">Sin resultado</option>
                <option value="defective">Producto defectuoso (confirmado)</option>
                <option value="ok">Producto funciona correctamente</option>
                <option value="damaged">Danado en transporte</option>
              </select>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              {caseType === 'doa' ? 'Notas del testeo' : 'Notas de cierre'}
            </label>
            <textarea
              value={testNotes}
              onChange={(e) => setTestNotes(e.target.value)}
              rows={3}
              placeholder={caseType === 'doa' ? 'Resultado del testeo...' : 'Resolucion de la incidencia...'}
              className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-sm"
            />
          </div>

          {error && (
            <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 dark:bg-red-900/20 p-3 rounded-lg">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 p-5 border-t border-slate-200 dark:border-slate-700">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900">
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="flex items-center gap-1.5 px-5 py-2.5 bg-slate-700 text-white rounded-lg hover:bg-slate-800 text-sm font-medium disabled:opacity-50"
          >
            {submitting ? (
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <CheckCircle className="w-4 h-4" />
            )}
            {submitting ? 'Cerrando...' : 'Cerrar incidencia'}
          </button>
        </div>
      </div>
    </div>
  )
}
